import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { CreditCard, FileText, DollarSign, Shield, Edit, Save, AlertTriangle } from "lucide-react";
import { formatEINInputValue } from "@/lib/ein";
import { formatSSNInput } from "@/lib/utils";
import { SsnInput } from "@/components/employees/SsnInput";
import { isMaskedSsn } from "@/lib/ssnReveal";
import { getPlanTypeMeta } from "@shared/schemas/planTypes";

interface BankingInfo {
  bankName?: string;
  routingNumber?: string;
  accountNumber?: string;
  accountType?: string;
  bankAccountVerified?: boolean;
}

interface TaxInfo {
  ssn?: string;
  taxId?: string;
  w9Status?: string;
  compensationType?: string;
  compensationAmount?: string;
  payCycle?: string;
  filingStatus?: string;
  taxAllowances?: number;
  additionalTaxWithholding?: number;
  taxExempt?: boolean;
}

interface Enrollment {
  id: number;
  planName: string;
  planType: string;
  planId: number;
  monthlyPremium: number;
  employeeContribution?: number;
  employerContribution?: number;
  taxDetermination?: string;
  employeeTaxChoice?: string;
  status: string;
  effectiveDate: string;
  canModify: boolean;
}

export default function BankAndTax() {
  const [isEditingBanking, setIsEditingBanking] = useState(false);
  const [isEditingTax, setIsEditingTax] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Fetch employee record for existing data
  const { data: employeeRecord } = useQuery<any>({
    queryKey: ["/api/employees/me"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/employees/me");
      return response.json();
    },
  });

  // Current benefit enrollments with SERVER-computed premiums. The server
  // (withComputedPremiums) resolves each enrollment's snapshotted eligibility
  // -group contribution rule set and buy-up base-plan rates — logic the client
  // cannot reproduce from plan/rate data alone (it would pick the wrong group's
  // contribution % and under/over-state the deduction). So we consume the
  // server's per-enrollment employeePremium directly rather than recomputing.
  const { data: enrollments = [] } = useQuery({
    queryKey: ["/api/employees", user?.employeeId, "benefits-payroll"],
    enabled: Boolean(user?.employeeId),
    queryFn: async () => {
      const res = await fetch(`/api/employees/${user?.employeeId}/benefits`);
      if (!res.ok) return [] as any[];
      const rows = (await res.json()) as any[];

      // The server attaches computed premiums to base rows (dependentId null);
      // dependent sub-rows carry the coverage but no premium of their own.
      return (Array.isArray(rows) ? rows : [])
        .filter(
          (r: any) =>
            r?.dependentId == null &&
            String(r?.status ?? "").toLowerCase() === "active"
        )
        .map((r: any) => {
          const plan = (r?.plan || {}) as any;
          const taxDetermination = plan?.taxDetermination || "pre-tax";
          return {
            id: r?.id,
            planId: r?.planId,
            planName: plan?.name,
            planType: plan?.type,
            coverageLevel: r?.coverageTier,
            monthlyPremium: Number(
              r?.employeePremium ?? r?.premium?.employeePremium ?? 0
            ),
            effectiveDate: r?.effectiveDate,
            status: r?.status,
            taxDetermination,
            employeeTaxChoice: taxDetermination, // Could be overridden by employee choice in future
          };
        });
    },
  });

  // Banking form
  const bankingForm = useForm<BankingInfo>({
    defaultValues: {
      bankName: "",
      routingNumber: "",
      accountNumber: "",
      accountType: "checking",
      bankAccountVerified: false
    }
  });

  // Tax form
  const taxForm = useForm<TaxInfo>({
    defaultValues: {
      ssn: "",
      taxId: "",
      w9Status: "",
      compensationType: "",
      compensationAmount: "",
      payCycle: "",
      filingStatus: "",
      taxAllowances: 0,
      additionalTaxWithholding: 0,
      taxExempt: false
    }
  });  // Reset forms when employee data loads
  useEffect(() => {
    if (employeeRecord) {
      bankingForm.reset({
        bankName: employeeRecord.bankName || "",
        routingNumber: employeeRecord.routingNumber || "",
        accountNumber: employeeRecord.accountNumber || "",
        accountType: employeeRecord.accountType || "checking",
        bankAccountVerified: employeeRecord.bankAccountVerified || false
      });
      
      taxForm.reset({
        ssn: employeeRecord.ssn || "",
        taxId: employeeRecord.taxId || "",
        w9Status: employeeRecord.w9Status || "",
        compensationType: employeeRecord.compensationType || "",
        compensationAmount: employeeRecord.compensationAmount || "",
        payCycle: employeeRecord.payCycle || "",
        filingStatus: employeeRecord.filingStatus || "",
        taxAllowances: employeeRecord.taxAllowances || 0,
        additionalTaxWithholding: employeeRecord.additionalTaxWithholding || 0,
        taxExempt: employeeRecord.taxExempt || false
      });
    }
  }, [employeeRecord, bankingForm, taxForm]);

  // Update banking information mutation
  const updateBankingMutation = useMutation({
    mutationFn: async (data: BankingInfo) => {
      const payload = {
        bankName: data.bankName || null,
        routingNumber: data.routingNumber || null,
        accountNumber: data.accountNumber || null,
        accountType: data.accountType || "checking",
        bankAccountVerified: data.bankAccountVerified || false
      };
      return await apiRequest("PATCH", "/api/employees/me", payload);
    },
    onSuccess: () => {
      toast({
        title: "Banking Information Updated",
        description: "Your banking information has been saved successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me"] });
      setIsEditingBanking(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update banking information. Please try again.",
        variant: "destructive",
      });
    }
  });

    // Update tax information mutation (update all available tax fields)
  const updateTaxMutation = useMutation({
    mutationFn: async (data: TaxInfo) => {
      const payload = {
        // Masked ***-**-1234 from the API = unchanged; undefined is not sent
        ssn: isMaskedSsn(data.ssn) ? undefined : data.ssn || null,
        filingStatus: data.filingStatus || null,
        taxAllowances: data.taxAllowances || null,
        additionalTaxWithholding: data.additionalTaxWithholding || null,
        taxExempt: data.taxExempt || false
      };
      return await apiRequest("PATCH", "/api/employees/me", payload);
    },
    onSuccess: () => {
      toast({
        title: "Tax & Compensation Information Updated", 
        description: "Your tax and compensation information has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me"] });
      setIsEditingTax(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update tax information. Please try again.",
        variant: "destructive",
      });
    }
  });

  const handleSaveBanking = bankingForm.handleSubmit((data) => {
    updateBankingMutation.mutate(data);
  });

  const handleSaveTax = taxForm.handleSubmit((data) => {
    updateTaxMutation.mutate(data);
  });

  // Calculate deductions from enrollments using actual tax determination data
  const preTaxDeductions = enrollments.filter(e => {
    // Use actual tax determination from plan configuration
    const taxDetermination = (e as any).taxDetermination?.toLowerCase();
    const employeeTaxChoice = (e as any).employeeTaxChoice?.toLowerCase();
    
    // Employee choice overrides plan default if available
    return (employeeTaxChoice || taxDetermination) === 'pre-tax';
  });
  
  const postTaxDeductions = enrollments.filter(e => {
    // Use actual tax determination from plan configuration
    const taxDetermination = (e as any).taxDetermination?.toLowerCase();
    const employeeTaxChoice = (e as any).employeeTaxChoice?.toLowerCase();
    
    // Employee choice overrides plan default if available
    return (employeeTaxChoice || taxDetermination) === 'post-tax';
  });
  
  const totalPreTax = preTaxDeductions.reduce((sum, enrollment) => {
    return sum + ((enrollment as any).monthlyPremium || 0);
  }, 0);
  
  const totalPostTax = postTaxDeductions.reduce((sum, enrollment) => {
    return sum + ((enrollment as any).monthlyPremium || 0);
  }, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Banking & Tax Information</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Direct Deposit
            </CardTitle>
            <div className="flex gap-2">
              {isEditingBanking ? (
                <>
                  <Button 
                    size="sm" 
                    onClick={handleSaveBanking}
                    disabled={updateBankingMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setIsEditingBanking(false);
                      bankingForm.reset();
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setIsEditingBanking(true)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveBanking}>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bankName">Bank Name</Label>
                  <Controller
                    name="bankName"
                    control={bankingForm.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="bankName"
                        disabled={!isEditingBanking}
                        placeholder="Enter bank name"
                      />
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accountType">Account Type</Label>
                  <Controller
                    name="accountType"
                    control={bankingForm.control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!isEditingBanking}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">Checking</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="routingNumber">Routing Number</Label>
                  <Controller
                    name="routingNumber"
                    control={bankingForm.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="routingNumber"
                        disabled={!isEditingBanking}
                        placeholder="9-digit routing number"
                        maxLength={9}
                      />
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accountNumber">Account Number</Label>
                  <Controller
                    name="accountNumber"
                    control={bankingForm.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="accountNumber"
                        disabled={!isEditingBanking}
                        placeholder="Account number"
                        type={isEditingBanking ? "text" : "password"}
                      />
                    )}
                  />
                </div>
              </div>
              {bankingForm.watch("bankAccountVerified") && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center gap-2 text-green-700">
                    <Shield className="h-4 w-4" />
                    <span className="text-sm font-medium">Account Verified</span>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Tax & Compensation Info
            </CardTitle>
            <div className="flex gap-2">
              {isEditingTax ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => setIsEditingTax(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveTax}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </>
              ) : (
                <Button variant='outline' size="sm" onClick={() => setIsEditingTax(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ssn">Social Security Number</Label>
                <Controller
                  name="ssn"
                  control={taxForm.control}
                  render={({ field }) =>
                    isEditingTax && employeeRecord?.id ? (
                      <SsnInput
                        id="ssn"
                        employeeId={employeeRecord.id}
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    ) : (
                    <Input
                      id="ssn"
                      {...field}
                      placeholder="***-**-1234"
                      type={isEditingTax ? "text" : "password"}
                      readOnly={!isEditingTax}
                      maxLength={11}
                      onChange={(e) => {
                        const formatted = formatSSNInput(e.target.value);
                        field.onChange(formatted);
                      }}
                    />
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor="taxId">Tax ID</Label>
                <Controller
                  name="taxId"
                  control={taxForm.control}
                  render={({ field }) => (
                    <Input
                      id="taxId"
                      {...field}
                      onChange={(e) => {
                        const formatted = formatEINInputValue(e.target.value);
                        field.onChange(formatted);
                      }}
                      placeholder="12-3456789"
                      readOnly={!isEditingTax}
                    />
                  )}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="w9Status">W-9 Status</Label>
              <Controller
                name="w9Status"
                control={taxForm.control}
                render={({ field }) => (
                  <Select 
                    value={field.value} 
                    onValueChange={field.onChange}
                    disabled={!isEditingTax}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select W-9 status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="received">Received</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="compensationType">Compensation Type</Label>
                <Controller
                  name="compensationType"
                  control={taxForm.control}
                  render={({ field }) => (
                    <Select 
                      value={field.value} 
                      onValueChange={field.onChange}
                      disabled={!isEditingTax}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="salary">Salary</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <Label htmlFor="compensationAmount">Compensation Amount</Label>
                <Controller
                  name="compensationAmount"
                  control={taxForm.control}
                  render={({ field }) => (
                    <Input 
                      id="compensationAmount" 
                      {...field}
                      placeholder="Amount"
                      readOnly={!isEditingTax}
                    />
                  )}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="filingStatus">Filing Status</Label>
                <Controller
                  name="filingStatus"
                  control={taxForm.control}
                  render={({ field }) => (
                    <Select 
                      value={field.value} 
                      onValueChange={field.onChange}
                      disabled={!isEditingTax}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select filing status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="married-filing-jointly">Married Filing Jointly</SelectItem>
                        <SelectItem value="married-filing-separately">Married Filing Separately</SelectItem>
                        <SelectItem value="head-of-household">Head of Household</SelectItem>
                        <SelectItem value="qualifying-widow">Qualifying Widow(er)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <Label htmlFor="taxAllowances">Tax Allowances</Label>
                <Controller
                  name="taxAllowances"
                  control={taxForm.control}
                  render={({ field }) => (
                    <Input 
                      id="taxAllowances" 
                      {...field}
                      type="number"
                      min="0"
                      placeholder="Number of allowances"
                      readOnly={!isEditingTax}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  )}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="additionalTaxWithholding">Additional Tax Withholding</Label>
                <Controller
                  name="additionalTaxWithholding"
                  control={taxForm.control}
                  render={({ field }) => (
                    <Input 
                      id="additionalTaxWithholding" 
                      {...field}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Additional amount per pay period"
                      readOnly={!isEditingTax}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  )}
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Controller
                  name="taxExempt"
                  control={taxForm.control}
                  render={({ field }) => (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="taxExempt"
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={!isEditingTax}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="taxExempt" className="text-sm">
                        Tax Exempt
                      </Label>
                    </div>
                  )}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="payCycle">Pay Cycle</Label>
              <Controller
                name="payCycle"
                control={taxForm.control}
                render={({ field }) => (
                  <Select 
                    value={field.value} 
                    onValueChange={field.onChange}
                    disabled={!isEditingTax}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select pay cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                      <SelectItem value="Bi-Weekly">Bi-Weekly</SelectItem>
                      <SelectItem value="Semi-Monthly">Semi-Monthly</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Payroll Deductions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3">Pre-Tax Deductions</h4>
                <div className="space-y-2">
                  {preTaxDeductions.length > 0 ? (
                    <>
                      {preTaxDeductions.map((enrollment: any) => (
                        <div key={enrollment.id} className="flex justify-between items-center">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{enrollment.planName}</span>
                            <span className="text-xs text-muted-foreground">
                              {getPlanTypeMeta(enrollment.planType).label} •
                              {enrollment.taxDetermination === 'pre-tax' ? ' Plan Default' : ' Employee Choice'}
                            </span>
                          </div>
                          <span className="text-sm font-medium">${(enrollment.monthlyPremium || 0).toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-medium border-t pt-2">
                        <span className="text-sm">Total Pre-Tax</span>
                        <span className="text-sm">${totalPreTax.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">No pre-tax deductions</p>
                  )}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-3">Post-Tax Deductions</h4>
                <div className="space-y-2">
                  {postTaxDeductions.length > 0 ? (
                    <>
                      {postTaxDeductions.map((enrollment: any) => (
                        <div key={enrollment.id} className="flex justify-between items-center">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{enrollment.planName}</span>
                            <span className="text-xs text-muted-foreground">
                              {getPlanTypeMeta(enrollment.planType).label} •
                              {enrollment.taxDetermination === 'post-tax' ? ' Plan Default' : ' Employee Choice'}
                            </span>
                          </div>
                          <span className="text-sm font-medium">${(enrollment.monthlyPremium || 0).toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-medium border-t pt-2">
                        <span className="text-sm">Total Post-Tax</span>
                        <span className="text-sm">${totalPostTax.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">No post-tax deductions</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tax Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No tax documents available yet</p>
            <p className="text-sm mt-2">Tax documents will appear here when they become available</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}